import { useState, useEffect, useRef } from 'react';

import Hamburger from 'hamburger-react';
import { AppBar, Box, IconButton, Toolbar, useTheme } from '@mui/material';
import { useRouter } from 'next/router';

import { App, BannerMobileAlertMessagesCollection, Header, Maybe } from '@/types/generated';
import ImageWrapper from '@/components/ImageWrapper';
import TAGLink from '@/components/TAGLink';
import Banner from '@/components/Banner/Banner';

import { bannerConfigExtractor } from '../HeaderDesktop/HeaderDesktop';
import LocationNavBar from '../LocationNavBar/LocationNavBar';
import NavigationBarItem from '../NavigationBar/NavigationBarItem/NavigationBarItem';

import {
	getWrapperStyles,
	logoStyles,
	iconStyles,
	toolbarHeaderStyles,
	burgerAndLogoWrapperStyles,
	headerNavRightStyles,
} from './HeaderMobile.styles';
import MobileMenu from './MobileMenu/MobileMenu';

interface IHeaderMobile {
	headerData: App['header'];
	logoUrl?: Maybe<string>;
	logoHref: string;
}

const RenderNavList = ({
	navList,
	isUtilityNavigation,
	linkColor,
	showIconText,
}: {
	navList: Header['navigationLinksCollection'] | Header['secondaryNavigationLinksCollection'];
	isUtilityNavigation?: boolean;
	linkColor?: string;
	showIconText?: boolean;
}) => {
	return (
		<>
			{navList?.items?.map(
				(item, index) =>
					item && (
						<NavigationBarItem
							key={item.sys.id}
							item={item}
							itemIndex={index}
							isMobile={true}
							linkColor={linkColor}
							isUtilityNavigation={isUtilityNavigation}
							iconSize={2}
							showIconText={showIconText}
							navLinkStyles={{
								display: 'inline-flex',
								alignItems: 'center',
							}}
						/>
					)
			)}
		</>
	);
};

export default function HeaderMobile({ logoUrl, logoHref = '/', headerData }: Readonly<IHeaderMobile>) {
	const theme = useTheme();
	const appBarRef = useRef<HTMLDivElement>(null);

	const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
	const router = useRouter();

	const {
		topAlertBannerMessages,
		bottomAlertBannerMessages,
		showTopAlertBannerMessages,
		showBottomAlertBannerMessages,
	} = bannerConfigExtractor(headerData, true);

	useEffect(() => {
		if (isNavMenuOpen) {
			document.documentElement.style.overflowY = 'hidden';
		} else {
			document.documentElement.style.overflowY = 'inherit';
		}
	}, [isNavMenuOpen]);

	useEffect(() => {
		const handleRouteChange = () => {
			setIsNavMenuOpen(false);
		};

		router.events.on('routeChangeComplete', handleRouteChange);

		return () => {
			router.events.off('routeChangeComplete', handleRouteChange);
		};
	}, [router.events]);

	return (
		<>
			<AppBar sx={getWrapperStyles(theme)} ref={appBarRef} data-testid="header_with_location_nav_mobile">
				{showTopAlertBannerMessages && (
					<Banner
						dataTestId="header_mobile_top_alert_banner"
						variant={'Alert Banner'}
						mobileAlertMessagesCollection={topAlertBannerMessages as BannerMobileAlertMessagesCollection}
					/>
				)}
				<Toolbar disableGutters sx={toolbarHeaderStyles} data-testid="header_with_location_nav_mobile_actions">
					<Box sx={burgerAndLogoWrapperStyles}>
						<TAGLink href={logoHref}>
							<ImageWrapper
								src={logoUrl}
								width="116px"
								height="auto"
								sx={logoStyles}
								data-testid="img_logo_mobile"
								alt="brand logo"
							/>
						</TAGLink>
						<Box sx={headerNavRightStyles}>
							{headerData?.showSecondaryNavInMobileToolbar && (
								<RenderNavList navList={headerData.secondaryNavigationLinksCollection} />
							)}
							<IconButton
								sx={iconStyles}
								data-testid="header_with_location_nav_burger_button"
								tabIndex={-1}
							>
								<Hamburger
									size={24}
									toggled={isNavMenuOpen}
									toggle={setIsNavMenuOpen}
									color={theme.palette.text.interactive}
									label="open menu"
								/>
							</IconButton>
						</Box>
					</Box>
				</Toolbar>
				<LocationNavBar
					schedulingButtonText={headerData?.schedulingButtonText}
					schedulingButtonTextMobile={headerData?.schedulingButtonTextMobile}
					schedulingButtonUrl={headerData?.schedulingButtonUrl}
					locationBarFindOfficeUrl={headerData?.locationBarFindOfficeUrl}
					searchFieldTitle={headerData?.searchFieldTitle}
					isMobile
				/>
				<div ref={appBarRef}></div>
				{showBottomAlertBannerMessages && (
					<Banner
						dataTestId="header_mobile_bottom_alert_banner"
						variant={'Alert Banner'}
						mobileAlertMessagesCollection={bottomAlertBannerMessages as BannerMobileAlertMessagesCollection}
					/>
				)}
			</AppBar>
			<MobileMenu appBarRef={appBarRef} onClose={() => setIsNavMenuOpen(false)} open={isNavMenuOpen}>
				<RenderNavList
					navList={headerData?.navigationLinksCollection}
					isUtilityNavigation={true}
					linkColor="text.interactive"
				/>
				{!headerData?.showSecondaryNavInMobileToolbar && (
					<RenderNavList
						navList={headerData?.secondaryNavigationLinksCollection}
						isUtilityNavigation={true}
						linkColor="text.interactive"
						showIconText={true}
					/>
				)}
			</MobileMenu>
		</>
	);
}
